import { create } from 'zustand';
import { 
    sendVehicles,
    sendVehicleUpsert,
    sendVehicleRemove,
    sendVehiclesTrip,
    sendVehiclesSelectedTrips 
} from '../channels/vehiclesChannel';

export const NO_VEHICLE_ID = 'NO_VEHICLE';

export const NO_VEHICLE_OPTION = {
    id: NO_VEHICLE_ID,
    label: 'Sem Veículo'
};

function sortVehicles(veiculos = []) {
    return [...veiculos].sort((a, b) =>
        String(a.label).localeCompare(
            String(b.label),
            'pt-BR'
        )
    );
}

function buildVehiclesState(veiculos = []) {
    
    const sorted = sortVehicles(veiculos);

    return {
        veiculosDisponiveis: sorted,

        veiculosDisponiveisMap: new Map(
            sorted.map(veiculo => [
                String(veiculo.id),
                veiculo
            ])
        ),
        veiculosComOpcaoSemVeiculo: [
            NO_VEHICLE_OPTION,
            ...sorted
        ]
    };

}

function buildAllowVeiculos(veiculosSelecionados) {

    return veiculosSelecionados === null
        ? null
        : new Set(
            (veiculosSelecionados || [])
            .map(String)
        );

}

function buildAllowVeiculosEmViagem(veiculosSelecionados) {

    return veiculosSelecionados === null
        ? null
        : new Set(
            (veiculosSelecionados || []).map(String)
        );

}

function getVeiculosEmViagemSelecionados() {

    try {

        const saved = localStorage.getItem(
            'veiculosEmViagemSelecionados'
        );

        return saved
            ? JSON.parse(saved)
            : null;

    } catch {

        return null;

    }

}

function saveVeiculosEmViagemSelecionados(veiculosSelecionados) {

    try {

        if (veiculosSelecionados === null) {

            localStorage.removeItem(
                'veiculosEmViagemSelecionados'
            );

            return;

        }

        localStorage.setItem(
            'veiculosEmViagemSelecionados',
            JSON.stringify(veiculosSelecionados)
        );

    } catch {}

}

function getVeiculosSelecionados() {

    try {

        const saved = localStorage.getItem('veiculosSelecionados');

        return saved
            ? JSON.parse(saved)
            : null;

    } catch {

        return null;

    }

}

function saveVeiculosSelecionados(veiculosSelecionados) {

    try {

        if (veiculosSelecionados === null) {

            localStorage.removeItem(
                'veiculosSelecionados'
            );

            return;

        }

        localStorage.setItem(
            'veiculosSelecionados',
            JSON.stringify(veiculosSelecionados)
        );

    } catch {}

}

const initialVeiculosSelecionados = getVeiculosSelecionados();
const initialVeiculosEmViagemSelecionados = getVeiculosEmViagemSelecionados();

export const useVehiclesStore = create((set) => ({
    
    veiculosDisponiveis: [],
    veiculosDisponiveisMap: new Map(),

    veiculosComOpcaoSemVeiculo: [NO_VEHICLE_OPTION],

    veiculosSelecionados: initialVeiculosSelecionados,
    allowVeiculos: buildAllowVeiculos(initialVeiculosSelecionados),

    veiculosEmViagem: [],
    veiculosEmViagemMap: new Map(),

    veiculosEmViagemSelecionados: initialVeiculosEmViagemSelecionados,
    allowVeiculosEmViagem: buildAllowVeiculosEmViagem(initialVeiculosEmViagemSelecionados),

    setVeiculosSelecionados: (veiculosSelecionados) => {

      saveVeiculosSelecionados(veiculosSelecionados);

      set({
        veiculosSelecionados,

        allowVeiculos: buildAllowVeiculos(veiculosSelecionados)
      });

    },

    setVeiculosEmViagemSelecionados: (veiculosSelecionados, broadcast = true) => {

        set(state => {

            let selecionados = veiculosSelecionados;

            if (
                selecionados !== null &&
                selecionados.length === state.veiculosEmViagem.length
            ) {
                selecionados = null;
            }

            saveVeiculosEmViagemSelecionados(selecionados);

            if(broadcast) {
                sendVehiclesSelectedTrips(veiculosSelecionados);
            }

            return {

                veiculosEmViagemSelecionados:
                    selecionados,

                allowVeiculosEmViagem:
                    buildAllowVeiculosEmViagem(
                        selecionados
                    )

            };

        });

    },

    setVehicles: (veiculos = [], broadcast = true) => {
        set(buildVehiclesState(veiculos));

        if(broadcast){
            sendVehicles(veiculos);
        }
    },

    upsertVehicle: (veiculo, broadcast = true) => {

        set((state) => {

            const map = new Map(state.veiculosDisponiveisMap);

            map.set(
                String(veiculo.id),
                veiculo
            );

            if(broadcast) {
                sendVehicleUpsert(veiculo);
            }

            return buildVehiclesState(
                Array.from(map.values())
            );

        });

    },

    removeVehicle: (veiculoId, broadcast = true) => {

        set((state) => {

            const map = new Map(state.veiculosDisponiveisMap);

            map.delete(String(veiculoId));

            if(broadcast) {
                sendVehicleRemove(veiculoId);
            }

            return buildVehiclesState(
                Array.from(map.values())
            );

        });

    },

    updateVeiculosEmViagem: (
        trips = [],
        broadcast = true
    ) => {

        set(state => {

            const veiculosEmViagem = [];
            const veiculosEmViagemMap = new Map();

            for (const trip of trips) {

                const placa = String(
                    trip.license_plate || ''
                ).trim();

                if (
                    !placa ||
                    veiculosEmViagemMap.has(placa)
                ) {
                    continue;
                }

                // =========================
                // SEM VEÍCULO
                // =========================

                if (placa === NO_VEHICLE_ID) {

                    veiculosEmViagem.push(
                        NO_VEHICLE_OPTION
                    );

                    veiculosEmViagemMap.set(
                        NO_VEHICLE_ID,
                        NO_VEHICLE_OPTION
                    );

                    continue;
                }

                const veiculo =
                    state.veiculosDisponiveisMap.get(
                        placa
                    );

                if (!veiculo) {
                    continue;
                }

                veiculosEmViagem.push(veiculo);

                veiculosEmViagemMap.set(
                    placa,
                    veiculo
                );

            }

            let selecionados =
                state.veiculosEmViagemSelecionados;

            if (selecionados !== null) {

                selecionados =
                    selecionados.filter(id =>
                        veiculosEmViagemMap.has(
                            String(id)
                        )
                    );

            }

            return {
                veiculosEmViagem,
                veiculosEmViagemMap,

                veiculosEmViagemSelecionados:
                    selecionados,

                allowVeiculosEmViagem:
                    buildAllowVeiculosEmViagem(
                        selecionados
                    )
            };

        });

        if (broadcast) {
            sendVehiclesTrip(trips);
        }

    },

    resetVehicles: () =>
        set({
            veiculosDisponiveis: [],
            veiculosDisponiveisMap: new Map(),
            veiculosComOpcaoSemVeiculo: [NO_VEHICLE_OPTION],
            veiculosSelecionados: null,
            allowVeiculos: null
        })

}));